/* ============================================================
   Kanda — entry point
   ============================================================ */

import './fonts.css';
import './style.css';

import { router } from './shared/router.js';
import { applyBrand } from './shared/brand.js';
import { mountStorageNotice } from './shared/storage-notice.js';
import { TOOLS, getToolByPath } from './data/tools.js';
import { Header } from './components/Header.js';
import { HomePage } from './pages/HomePage.js';
import { PlannedPage, NotFoundPage } from './pages/PlannedPage.js';
import { AboutPage } from './pages/AboutPage.js';
import { AboutUsPage } from './pages/AboutUsPage.js';
import { ProfilePage } from './pages/ProfilePage.js';
import { CompareTool } from './tools/compare/index.js';
import { PrivacyPage, TermsPage, FaqPage } from './pages/LegalPages.js';
import { Footer } from './components/Footer.js';

import { ScorecardPage } from './tools/a1-scorecard/index.js';
import { BenchmarkPage } from './tools/a2-benchmark/index.js';
import { CompliancePage } from './tools/compliance/index.js';
import { GlossaryPage } from './tools/glossary/index.js';
import { LicensingPage } from './tools/v1-licensing/index.js';
import { OrganogramPage } from './tools/v4-organogram/index.js';
import { SezPage } from './tools/v5-sez/index.js';
import { CapitalPage } from './tools/capital/index.js';
import { StructurePage } from './tools/structure/index.js';
import { LandedCostPage } from './tools/landed-cost/index.js';
import { ProfitabilityPage } from './tools/profitability/index.js';
import { ReadinessPage } from './tools/readiness/index.js';
import { SavedPage } from './tools/saved/index.js';
import { EmissionsPage } from './tools/emissions/index.js';
import { NtbPage } from './tools/ntb/index.js';
import { FinanceabilityPage } from './tools/financeability/index.js';
import { CorridorRunPage } from './tools/corridor-run/index.js';
import { ImplementationPage } from './tools/implementation/index.js';

/** Live tools, keyed by registry id. */
const VIEWS = {
  compare: CompareTool,
  scorecard: ScorecardPage,
  benchmark: BenchmarkPage,
  compliance: CompliancePage,
  'landed-cost': LandedCostPage,
  profitability: ProfitabilityPage,
  readiness: ReadinessPage,
  saved: SavedPage,
  emissions: EmissionsPage,
  ntb: NtbPage,
  financeability: FinanceabilityPage,
  'corridor-run': CorridorRunPage,
  glossary: GlossaryPage,
  licensing: LicensingPage,
  organogram: OrganogramPage,
  sez: SezPage,
  capital: CapitalPage,
  structure: StructurePage,
  implementation: ImplementationPage
};

function boot() {
  applyBrand();
  /* Before anything can try to save. The storage layer never throws, so
     without this a refused write is a console warning and nothing
     else — and the user finds out by opening /saved/ and not finding
     their run. */
  mountStorageNotice();

  const app = document.getElementById('app');
  app.replaceChildren();
  app.appendChild(Header());

  const outlet = document.createElement('main');
  outlet.id = 'outlet';
  // -1 so the skip link moves focus rather than only scrolling, without
  // adding the outlet to the natural tab order.
  outlet.tabIndex = -1;
  app.appendChild(outlet);

  router.register('/', HomePage, {
    title: 'Dashboard',
    description:
      'Corridor diagnostics, landed cost, demurrage exposure and every Kenyan regulatory ' +
      'deadline that applies to you. Free, no account, and nothing leaves your browser.'
  });
  router.register('/about', AboutPage, { title: 'Method & sources' });
  router.register('/about-us', AboutUsPage, { title: 'About us' });
  router.register('/profile', ProfilePage, { title: 'Company profile' });
  router.register('/privacy', PrivacyPage, { title: 'Privacy Notice' });
  router.register('/terms', TermsPage, { title: 'Terms of Use' });
  router.register('/faq', FaqPage, { title: 'FAQ' });

  // Routes come from the registry, so a card cannot exist without a
  // route and a planned tool renders an honest state (audit A-22).
  for (const tool of TOOLS) {
    const view = VIEWS[tool.id];
    if (tool.status === 'live' && view) {
      router.register(tool.path, view, {
        title: tool.title,
        description: tool.description
      });
    } else {
      router.register(tool.path, () => PlannedPage(tool), {
        title: tool.title,
        description: tool.description
      });
    }
  }

  router.setNotFound((path) => {
    const tool = getToolByPath(path);
    return tool ? PlannedPage(tool) : NotFoundPage(path);
  });

  app.appendChild(Footer());

  router.start(outlet);
}

/* ---- Service worker ---- */
function registerServiceWorker() {
  if (!('serviceWorker' in navigator)) return;
  if (import.meta.env?.DEV) return; // the dev server serves fresh assets

  window.addEventListener('load', () => {
    navigator.serviceWorker.register('/sw.js').catch((error) => {
      console.warn('Service worker registration failed:', error);
    });
  });
}

/* ---- Global safety net ---- */
window.addEventListener('error', (event) => {
  console.error('Uncaught error:', event.error || event.message);
});

window.addEventListener('unhandledrejection', (event) => {
  console.error('Unhandled rejection:', event.reason);
});

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', boot, { once: true });
} else {
  boot();
}

registerServiceWorker();
